/* ============================================================
 * Formula Catalogue — single source of truth for all cost formulas
 * used across CostIQ. Each entry powers (a) the inline FormulaInfo
 * tooltip and (b) the Formula Registry admin page.
 * ============================================================ */

export const FORMULAS = [
    // ---------- ZBC engine constants (editable) ----------
    {
        id: "scrap_recoverable",
        section: "ZBC · Material",
        field: "Scrap Credit",
        expression: "scrap_credit = (input_weight − finish_weight) × SCRAP_RECOVERABLE × scrap_rate",
        current_value: 0.96,
        value_label: "SCRAP_RECOVERABLE",
        editable: true,
        impact_areas: ["ZBC Net Material Cost", "ZBC Should-Cost", "RFQ Final Price (all variants)"],
        notes: "Fraction of scrap that is sellable. 0.96 = 4% irrecoverable.",
    },
    {
        id: "zbc_icc_pct",
        section: "ZBC · Allowances",
        field: "ICC %",
        expression: "icc = input_rm_cost × ICC_PCT / 100\n(where input_rm_cost = input_weight × rm_rate)",
        current_value: 1.0,
        value_label: "ICC_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost", "Variance Badge"],
        notes: "Inward Cost Compensation. Applied on Input RM Cost (DemoClient standard, matches SAPL/PUN/ZBC template cell T9 = O9 × 1%).",
    },
    {
        id: "zbc_rejection_pct",
        section: "ZBC · Allowances",
        field: "Rejection %",
        expression: "rejection = base × REJECTION_PCT / 100",
        current_value: 0.25,
        value_label: "REJECTION_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
    },
    {
        id: "zbc_tool_maint_pct",
        section: "ZBC · Allowances",
        field: "Tool Maintenance %",
        expression: "tool_maint = conversion_cost × TOOL_MAINT_PCT / 100",
        current_value: 0.0,
        value_label: "TOOL_MAINT_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
        notes: "Disabled by default (0%) per DemoClient practice — cell U9 blank in every part sheet of the pilot workbook. Enable per-tenant if required.",
    },
    {
        id: "zbc_profit_oh_pct",
        section: "ZBC · Allowances",
        field: "Profit + Overhead %",
        expression: "profit_oh = base × PROFIT_OH_PCT / 100",
        current_value: 5.0,
        value_label: "PROFIT_OH_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
    },
    {
        id: "zbc_pf_pct",
        section: "ZBC · Allowances",
        field: "P&F %",
        expression: "pf = sub_total × PF_PCT / 100",
        current_value: 0.4,
        value_label: "PF_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
        notes: "Packing & Forwarding. Applied on Sub Total (matches DemoClient template cell W9 = S9 × 0.4%).",
    },
    {
        id: "zbc_profit_pct",
        section: "ZBC · Allowances",
        field: "Profit %",
        expression: "profit = sub_total × PROFIT_PCT / 100",
        current_value: 3.0,
        value_label: "PROFIT_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
        notes: "Matches DemoClient template cell X9 = S9 × 3%.",
    },
    {
        id: "zbc_oh_pct",
        section: "ZBC · Allowances",
        field: "Overhead %",
        expression: "oh = sub_total × OH_PCT / 100",
        current_value: 2.0,
        value_label: "OH_PCT",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
        notes: "Matches DemoClient template cell Y9 = S9 × 2%.",
    },
    {
        id: "zbc_tpt_per_kg",
        section: "ZBC · Allowances",
        field: "RM TPT (₹/kg)",
        expression: "tpt = input_weight_kg × TPT_PER_KG",
        current_value: 0.5,
        value_label: "TPT_PER_KG",
        editable: true,
        impact_areas: ["ZBC Should-Cost"],
        notes: "Freight per-kg of Input Weight (₹/kg). Matches DemoClient template cell Z9 = J9 × 0.5. Admins can raise this for distant plants.",
    },
    {
        id: "unit_wk_utilization_pct",
        section: "ZBC · Cycle Time",
        field: "Unit WK Utilisation %",
        expression: "unit_cost = corp_cost / (UNIT_WK_UTILIZATION_PCT / 100)\n(applies to conversion cost only)",
        current_value: 78.0,
        value_label: "UNIT_WK_UTILIZATION_PCT",
        editable: true,
        impact_areas: ["ZBC Intelligent Wizard · Unit vs Corp Twin View"],
        notes: "DemoClient two-school pattern. Corp WK = engineered cycle time. Unit WK = engineered ÷ this factor to model setup + changeovers + OEE losses. 78% is the industry benchmark for a well-run CNC shop; raise to 85–90% for automated cells, drop to 65–70% for manual welding.",
    },
    {
        id: "round_bar_machining_loss_pct",
        section: "ZBC · Finish Weight (Auto-Derive)",
        field: "Round Bar Machining Loss %",
        expression: "finish_weight_kg = input_weight_kg × (1 − ROUND_BAR_MACHINING_LOSS_PCT / 100)",
        current_value: 45.0,
        value_label: "ROUND_BAR_MACHINING_LOSS_PCT",
        editable: true,
        impact_areas: ["Manual ZBC · Finish Weight", "Intelligent Wizard · Finish Weight fallback"],
        notes: "Typical machining loss when a Ø-something bar is turned into a bush / port / pin / bolt. Applied ONLY when the user hasn't entered Finish Weight and the Intelligent Wizard didn't extract a title-block weight note. Confidence chip shows MEDIUM. Range 30–60% is typical.",
    },
    {
        id: "prismatic_machining_loss_pct",
        section: "ZBC · Finish Weight (Auto-Derive)",
        field: "Square/Hex Bar Machining Loss %",
        expression: "finish_weight_kg = input_weight_kg × (1 − PRISMATIC_MACHINING_LOSS_PCT / 100)",
        current_value: 40.0,
        value_label: "PRISMATIC_MACHINING_LOSS_PCT",
        editable: true,
        impact_areas: ["Manual ZBC · Finish Weight", "Intelligent Wizard · Finish Weight fallback"],
        notes: "Typical machining loss when a square / hex bar is milled into a finished part. Applied ONLY when Finish Weight isn't entered and no drawing weight found. Range 30–55%.",
    },
    {
        id: "pipe_machining_loss_pct",
        section: "ZBC · Finish Weight (Auto-Derive)",
        field: "Pipe/Tube Machining Loss %",
        expression: "finish_weight_kg = input_weight_kg × (1 − PIPE_MACHINING_LOSS_PCT / 100)",
        current_value: 10.0,
        value_label: "PIPE_MACHINING_LOSS_PCT",
        editable: true,
        impact_areas: ["Manual ZBC · Finish Weight", "Intelligent Wizard · Finish Weight fallback"],
        notes: "Pipes and tubes usually only need end-face + cross-drill machining, so material loss is small. Range 5–15%.",
    },
    {
        id: "should_cost",
        section: "ZBC · Should-Cost",
        field: "Should-Cost",
        expression: "should_cost = base + (icc + rejection + tool_maint + profit_oh)\nbase = net_material + filling_material + conversion + cutting + welding",
        editable: false,
        impact_areas: ["Variance Badge", "RFQ Cost Build-Up"],
    },
    // ---------- Cutting / Welding (read-only — driven by Process Rates) ----------
    {
        id: "cutting_cost",
        section: "ZBC · Cutting",
        field: "Cutting Cost",
        expression: "cutting_cost = cut_perimeter_mtr × thickness_mm × cutting_rate",
        editable: false,
        source_screen: { label: "Process Rates", path: "/foundation/process-rates" },
        impact_areas: ["ZBC Should-Cost"],
        notes: "Cutting rate looked up from Process Rates (round-UP by thickness; Volvo flat 3.60/mtr/mm).",
    },
    {
        id: "welding_cost",
        section: "ZBC · Welding",
        field: "Welding Cost",
        expression: "joint_cost = (length_mm / 1000) × rate × qty × length_multiplier\nwelding_cost = Σ joint_cost",
        editable: false,
        source_screen: { label: "Process Rates", path: "/foundation/process-rates" },
        impact_areas: ["ZBC Should-Cost"],
        notes: "Rate: per-joint rate_override (Volvo) → customer-tagged ARGON_CO2 round-UP → generic MIG round-UP.",
    },
    // ---------- Volvo split-basis commercial (norm-driven; not editable here) ----------
    {
        id: "volvo_icc",
        section: "RFQ · VOLVO_EXPORT",
        field: "ICC (split-basis)",
        expression: "icc = (rm_steel + rm_filling + bop_cost) × icc_pct / 100",
        editable: false,
        source_screen: { label: "Customers & Norms", path: "/foundation/customers" },
        impact_areas: ["Volvo RFQ Final Price"],
        notes: "icc_pct is per-customer. Edit at Customers & Norms → DEMO_Volvo CE India.",
    },
    {
        id: "volvo_overhead",
        section: "RFQ · VOLVO_EXPORT",
        field: "Overhead",
        expression: "overhead = process_cost × overhead_pct / 100",
        editable: false,
        source_screen: { label: "Customers & Norms", path: "/foundation/customers" },
        impact_areas: ["Volvo RFQ Final Price"],
    },
    {
        id: "volvo_profit",
        section: "RFQ · VOLVO_EXPORT",
        field: "Profit (net of RM-rate adjustment)",
        expression: "profit_notional = profit_notional_base × profit_pct / 100\nrm_rate_adj = −(rm_new − rm_old) × profit_pct / 100\nprofit = profit_notional + rm_rate_adj",
        editable: false,
        source_screen: { label: "Customers & Norms", path: "/foundation/customers" },
        impact_areas: ["Volvo RFQ Final Price"],
    },
    {
        id: "volvo_grand_total",
        section: "RFQ · VOLVO_EXPORT",
        field: "Grand Total",
        expression: "grand_total = profit_notional_base + icc + rejection + overhead + profit + tpt",
        editable: false,
        impact_areas: ["Volvo RFQ Final Price"],
    },
    {
        id: "volvo_final_price",
        section: "RFQ · VOLVO_EXPORT",
        field: "Final Price",
        expression: "after_discount = grand_total − (grand_total × discount_pct / 100)\nfinal_price = after_discount − volume_rebate_rs",
        editable: false,
        impact_areas: ["Customer Invoice", "Settlement"],
    },

    // ============================================================
    // AUTO-CALCULATION LAYER (Manual ZBC form)
    // Every helper in /lib/zbcAutoCalc.js reads its numeric constants
    // from this registry via useAutoCalcConfig(). Editable coefficients
    // marked `editable: true` are Corporate-Admin / Finance-Pricing only.
    // ============================================================
    {
        id: "autocalc_offal_mm",
        section: "Auto-Calc · Sheet",
        field: "Blank Offal (mm/side)",
        expression: "gross_width  = blank_width  + 2 × offal_mm\ngross_length = blank_length + 2 × offal_mm",
        current_value: 5.0,
        value_label: "OFFAL_MM",
        editable: true,
        impact_areas: ["ZBC Input Weight", "ZBC Net Material Cost", "ZBC Should-Cost"],
        notes: "Extra margin added on each side of a sheet blank for laser-cutting kerf + handling. Standard 5 mm/side for laser, 8 mm/side for plasma.",
    },
    {
        id: "autocalc_density_steel",
        section: "Auto-Calc · Density",
        field: "Steel Density (g/cc)",
        expression: "input_weight = geometric_volume × steel_density / 1,000,000",
        current_value: 7.85,
        value_label: "STEEL_DENSITY_G_PER_CC",
        editable: true,
        impact_areas: ["ZBC Input Weight (all steel grades)"],
        notes: "Default density for carbon / structural / HSLA steel grades. Very rarely changed.",
    },
    {
        id: "autocalc_density_aluminium",
        section: "Auto-Calc · Density",
        field: "Aluminium Density (g/cc)",
        expression: "Same formula as steel, applied when Grade Code contains 'AL'.",
        current_value: 2.70,
        value_label: "AL_DENSITY_G_PER_CC",
        editable: true,
        impact_areas: ["ZBC Input Weight (aluminium grades — future)"],
    },
    {
        id: "autocalc_density_brass",
        section: "Auto-Calc · Density",
        field: "Brass / Copper Density (g/cc)",
        expression: "Same formula as steel, applied when Grade Code starts with 'BRASS' or 'CU'.",
        current_value: 8.50,
        value_label: "BRASS_DENSITY_G_PER_CC",
        editable: true,
        impact_areas: ["ZBC Input Weight (brass / copper grades — future)"],
    },
    {
        id: "autocalc_sheet_input_weight",
        section: "Auto-Calc · Sheet",
        field: "Sheet Blank Input Weight",
        expression: "input_weight_kg = (W + 2·offal) × (L + 2·offal) × T × density / 1,000,000",
        editable: false,
        source_screen: { label: "Blank Offal + Density", path: "/formula-registry" },
        impact_areas: ["ZBC Section 2 (Material)"],
        notes: "Reactive — auto-fills the moment Width + Length + Thickness are all entered. Includes the 5 mm/side offal.",
    },
    {
        id: "autocalc_bar_input_weight",
        section: "Auto-Calc · Round Bar",
        field: "Round Bar Input Weight",
        expression: "input_weight_kg = π × OD² × part_length × density / 4,000,000",
        editable: false,
        impact_areas: ["ZBC Section 2 (Material)"],
    },
    {
        id: "autocalc_tube_input_weight",
        section: "Auto-Calc · Square Tube",
        field: "Square Tube Input Weight",
        expression: "area_mm2   = a² − (a − 2t)²   (a = tube side, t = wall thickness)\ninput_weight_kg = area × tube_length × density / 1,000,000",
        editable: false,
        impact_areas: ["ZBC Section 2 (Material)"],
        notes: "Parses tube size strings like '40×40×2' or '40x40x2'.",
    },
    {
        id: "autocalc_finish_weight",
        section: "Auto-Calc · Weight",
        field: "Finish Weight",
        expression: "finish_weight_kg = input_weight × (1 − recoverable% − irrecoverable% − burning_loss%) / 100",
        editable: false,
        source_screen: { label: "Scrap Rates (RECOVERABLE_PCT, IRRECOVERABLE_PCT, BURNING_LOSS_LASER/PLASMA)", path: "/foundation/scrap-rates" },
        impact_areas: ["ZBC Section 2 (Material)", "Machine capacity planning"],
        notes: "Percentages are read from Scrap Rates rows with unit=PCT. Burning-loss is laser vs plasma depending on the chosen cutting process.",
    },
    {
        id: "autocalc_cut_perimeter",
        section: "Auto-Calc · Cutting",
        field: "Cut Perimeter (rectangular)",
        expression: "cut_perimeter_mtr = 2 × (blank_width + blank_length) / 1000",
        editable: false,
        impact_areas: ["ZBC Section 4 (Cutting Cost)"],
        notes: "Assumes a rectangular blank. Adjust manually to account for cut-outs, slots, or notches.",
    },
    {
        id: "autocalc_cutting_threshold_laser",
        section: "Auto-Calc · Cutting",
        field: "Laser Cutting Threshold (mm)",
        expression: "if  thickness ≤ LASER_MAX_MM  →  LASER_CUTTING\nelif thickness ≤ PLASMA_MAX_MM →  PLASMA_CUTTING\nelse                            →  GAS_CUTTING",
        current_value: 10.0,
        value_label: "LASER_MAX_MM",
        editable: true,
        impact_areas: ["ZBC Cutting Process suggestion"],
        notes: "Upper thickness limit at which fibre laser is still economical.",
    },
    {
        id: "autocalc_cutting_threshold_plasma",
        section: "Auto-Calc · Cutting",
        field: "Plasma Cutting Threshold (mm)",
        expression: "See Laser Cutting Threshold — parts thicker than PLASMA_MAX_MM auto-suggest GAS_CUTTING.",
        current_value: 16.0,
        value_label: "PLASMA_MAX_MM",
        editable: true,
        impact_areas: ["ZBC Cutting Process suggestion"],
    },
    {
        id: "autocalc_fillet_multiplier",
        section: "Auto-Calc · Welding",
        field: "Fillet Size Multiplier",
        expression: "fillet_mm = clamp( round(FILLET_MULT × thickness), FILLET_MIN, FILLET_MAX )",
        current_value: 0.7,
        value_label: "FILLET_MULT",
        editable: true,
        impact_areas: ["ZBC Section 5 (Welding Cost)"],
        notes: "Structural rule of thumb per IS 800 / AWS D1.1 minimum-fillet guidance.",
    },
    {
        id: "autocalc_fillet_min_mm",
        section: "Auto-Calc · Welding",
        field: "Fillet Minimum (mm)",
        expression: "See Fillet Size Multiplier.",
        current_value: 3.0,
        value_label: "FILLET_MIN",
        editable: true,
        impact_areas: ["ZBC Section 5 (Welding Cost)"],
    },
    {
        id: "autocalc_fillet_max_mm",
        section: "Auto-Calc · Welding",
        field: "Fillet Maximum (mm)",
        expression: "See Fillet Size Multiplier.",
        current_value: 6.0,
        value_label: "FILLET_MAX",
        editable: true,
        impact_areas: ["ZBC Section 5 (Welding Cost)"],
    },
    {
        id: "cycle_laser_speed_mm_s",
        section: "Intelligent Wizard · Cycle Time",
        field: "Laser Cutting Speed (mm/s)",
        expression: "cycle_time_seconds = perimeter_mm / LASER_SPEED_MM_S",
        current_value: 30.0,
        value_label: "LASER_SPEED_MM_S",
        editable: true,
        impact_areas: ["Intelligent ZBC Wizard · Conversion Cost (Laser Cutting)"],
        notes: "DemoClient shop-average fibre-laser speed used to estimate cycle time from intelligently-extracted geometry. Raise for thin gauge, lower for thick/high-strength grades.",
    },
    {
        id: "cycle_mig_weld_speed_mm_s",
        section: "Intelligent Wizard · Cycle Time",
        field: "MIG Weld Speed (mm/s)",
        expression: "cycle_time_seconds = joint_length_mm / MIG_WELD_SPEED_MM_S",
        current_value: 6.0,
        value_label: "MIG_WELD_SPEED_MM_S",
        editable: true,
        impact_areas: ["Intelligent ZBC Wizard · Conversion Cost (Welding)"],
        notes: "DemoClient shop-average MIG travel speed. Larger fillet sizes run slower in practice — override per-ZBC if needed.",
    },
    {
        id: "cycle_weld_mm_per_symbol",
        section: "Intelligent Wizard · Cycle Time",
        field: "Weld Length per Symbol (mm)",
        expression: "joint_length_mm = WELD_MM_PER_SYMBOL × count(welding_symbols)",
        current_value: 200.0,
        value_label: "WELD_MM_PER_SYMBOL",
        editable: true,
        impact_areas: ["Intelligent ZBC Wizard · Conversion Cost (Welding)"],
        notes: "Placeholder estimate used when the intelligent extraction detects weld symbols but no explicit joint length; override on the ZBC once the real length is known.",
    },
    {
        id: "capex_per_piece",
        section: "ZBC · CAPEX",
        field: "CAPEX per Piece",
        expression: "capex_per_piece = tooling_cost_rs ÷ (annual_volume × amortization_years)",
        editable: false,
        impact_areas: ["ZBC Should-Cost", "RFQ Final Price"],
        notes: "Amortises one-time tooling / fixture / die cost across the projected annual volume × years.",
    },
    {
        id: "capex_default_amort_years",
        section: "ZBC · CAPEX",
        field: "Default Amortisation Years",
        expression: "Used only when the user leaves the field blank on the ZBC form.",
        current_value: 3,
        value_label: "CAPEX_DEFAULT_YEARS",
        editable: true,
        impact_areas: ["ZBC CAPEX per Piece"],
    },
    {
        id: "children_rollup",
        section: "ZBC · Sub-Assemblies",
        field: "Child ZBC Roll-up",
        expression: "children_cost = Σ (child.should_cost × qty_per_assembly)\nassembly_should_cost += children_cost",
        editable: false,
        impact_areas: ["ZBC Should-Cost (parent)"],
        notes: "Each child's Should-Cost is snapshotted at save. Circular references are rejected by the server.",
    },
    {
        id: "autocalc_scrap_mapping",
        section: "Auto-Calc · Scrap",
        field: "Scrap Type Suggestion (from Grade)",
        expression: "IS 513 / *CR*             →  CR-SCRAP\nHARDOX / DOMEX            →  HARDOX-SCRAP\nSAE / EN-* (bars)         →  TURNINGS\nEverything else           →  MS-HEAVY",
        editable: false,
        source_screen: { label: "Scrap Rates", path: "/foundation/scrap-rates" },
        impact_areas: ["ZBC Section 2 (Material)"],
        notes: "Rules are code-based classifiers; adjust the underlying rates in Scrap Rates.",
    },
    {
        id: "autocalc_rm_category_mapping",
        section: "Auto-Calc · RM Category",
        field: "RM Category Suggestion (from Grade)",
        expression: "IS 4923 / IS 1239 / 'HOLLOW SECTION' → SQUARE_TUBE\nSAE + IS 1875 / 'ROUND BAR'         → ROUND_BAR\nIS 2062 / IS 513 / 'PLATE' etc.     → SHEET_STEEL",
        editable: false,
        impact_areas: ["ZBC Section 1 (Identity)"],
    },
    {
        id: "autocalc_machine_mapping",
        section: "Auto-Calc · Conversion",
        field: "Machine Suggestion (from Op Name)",
        expression: "keyword('laser')  → LASER\nkeyword('bend'/'brake') → CNC_PRESS_BRAKE\nkeyword('weld')   → ROBOT_WELDER | MIG_WELDER\nkeyword('drill')  → DRILL\nkeyword('paint')  → PAINT_LINE",
        editable: false,
        source_screen: { label: "Machines", path: "/foundation/machines" },
        impact_areas: ["ZBC Section 3 (Conversion Cost)"],
        notes: "Picks the first ACTIVE machine of the matching type at the ZBC's plant.",
    },
];

export const FORMULA_BY_ID = Object.fromEntries(FORMULAS.map((f) => [f.id, f]));
